
export const Mechanic = {
  ROULETTE: 'ROULETTE',
  SCRATCH: 'SCRATCH',
  SLOT: 'SLOT',
  PENALTY: 'PENALTY',
} as const;

export type Mechanic = (typeof Mechanic)[keyof typeof Mechanic];

export const CampaignType = Mechanic;
export type CampaignType = Mechanic;

const COLUMN_TO_MECHANIC: Record<string, Mechanic> = {
  ROULETTE: Mechanic.ROULETTE,
  WORLD_CUP: Mechanic.PENALTY,
  PENALTY: Mechanic.PENALTY,
  SCRATCH: Mechanic.SCRATCH,
  SLOT: Mechanic.SLOT,
};

export function normalizeMechanic(value: unknown): Mechanic {
  if (typeof value !== "string" || !value) return Mechanic.ROULETTE;
  return COLUMN_TO_MECHANIC[value.trim().toUpperCase()] ?? Mechanic.ROULETTE;
}

export const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  ROULETTE: 'Roleta',
  SCRATCH: 'Raspadinha',
  SLOT: 'Caça-níquel',
  PENALTY: 'Gol Premiado',
};

export const CAMPAIGN_TYPE_DESCRIPTIONS: Record<CampaignType, string> = {
  ROULETTE: 'Sorteio aleatório entre os participantes.',
  SCRATCH: 'Raspe o card e revele o prêmio.',
  SLOT: 'Gire os rolos do caça-níquel.',
  PENALTY: 'Chute ao gol com prêmios aleatórios.',
};

export enum GamificationStatus {
  ATIVA = 'ATIVA',
  INATIVA = 'INATIVA',
  AGENDADA = 'AGENDADA',
  ENCERRADA = 'ENCERRADA',
  RASCUNHO = 'RASCUNHO',
}

export enum GamificationSortBy {
  START_DATE = 'startDate',
  CAMPAIGN_NAME = 'campaignName',
  END_DATE = 'endDate',
  CREATED_AT = 'createdAt',
  STATUS = 'status',
  ID_CAPA = 'idCapa',
}

export interface IStore {
  id?: number;
  storeCode: string;
  storeName: string;
  nickname: string;
}

export interface IProbability {
  prob: number;
  prizes: number[];
}

export interface IGamificationStoreDto {
  code: number;
  name: string;
  nickname: string;
}

export interface IPrize {
  id?: number;
  descricao: string;
  probabilidade: number;
  quantidade: number;
}

export interface IPrizeRow {
  value: number;
  probability: number;
  label?: string;
}

export interface IFaqItem {
  question: string;
  answer: string;
}

export const VoucherValidityMode = {
  DAYS: 'DAYS',
  END_OF_NEXT_MONTH: 'END_OF_NEXT_MONTH',
  CAMPAIGN_END: 'CAMPAIGN_END',
} as const;

export type VoucherValidityMode =
  (typeof VoucherValidityMode)[keyof typeof VoucherValidityMode];

export const VOUCHER_VALIDITY_MODE_LABELS: Record<VoucherValidityMode, string> =
  {
    DAYS: 'Dias após a emissão',
    END_OF_NEXT_MONTH: 'Até o fim do mês seguinte',
    CAMPAIGN_END: 'Até o fim da campanha',
  };

export interface IVoucherValidity {
  mode?: VoucherValidityMode;

  validityDays?: number;

  startOffsetDays?: number;
}

export interface IConcurrency {
  competes: boolean;
  group?: string | null;
}

export interface ICpfSegmentation {
  enabled: boolean;
}

export interface ISlotSymbol {
  id: string;
  label: string;
  value: number;

  icon?: string;
}

export interface IPresentation {

  customerFacingName?: string;

  customerFacingDescription?: string;
}

export interface IMechanicParams {

  goalProbability?: number;

  symbols?: ISlotSymbol[];
}

export interface IMechanicConfig {
  prizes?: IPrizeRow[];
  maxPlays?: number;
  minPurchase?: number;
  priority?: number;
  concurrency?: IConcurrency;
  voucherValidity?: IVoucherValidity;
  cpfSegmentation?: ICpfSegmentation;
  mechanicParams?: IMechanicParams;
  presentation?: IPresentation;
}

export interface IGamification {
  id: number;
  idCapa?: number;
  nickname: string;

  slug?: string;
  startDate: string;
  endDate: string;
  status: GamificationStatus;
  campaignType: CampaignType;

  mechanic?: Mechanic;
  mechanicConfig?: IMechanicConfig;
  stores: IStore[];
  allStoresSelected?: boolean;
  faqItems?: IFaqItem[];
  regulations?: string;
  hasRegulationFile: boolean;
  hasBanner3x4: boolean;
  hasBanner16x9: boolean;
  banner3x4Url?: string;
  banner16x9Url?: string;
  probabilities: IProbability[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface IGamificationCampaignDto {
  id: number;
  idCapa?: number;
  campaignName: string;
  description?: string;
  slug?: string | null;
  stores: IGamificationStoreDto[];
  startDate: string;
  endDate: string;
  discountProbabilities: IProbability[] | { intervals: IProbability[] };
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  status: string;
  campaignType: string;
  mechanic: Mechanic;
  mechanicConfig?: IMechanicConfig;
  faqItems?: IFaqItem[];
  regulations?: string;
  hasRegulationFile: boolean;
  hasBanner3x4: boolean;
  hasBanner16x9: boolean;
  banner3x4Url?: string;
  banner16x9Url?: string;
  allStoresSelected: boolean;
}

export interface IGamificationListResponse {
  message: string;
  data: IGamificationCampaignDto[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  };
}

export interface IGamificationParams {
  meta: {
    page: number;
    perPage?: number;
  };
  search?: string;
  status?: string;
  orderBy?: string;
  campaignName?: string;
  startDate?: string;
  endDate?: string;
  sortBy?: GamificationSortBy;
  sortOrder?: 'ASC' | 'DESC';
  includeDeleted?: boolean;
  campaignType?: CampaignType;
}

export interface ICompetingCampaignsParams {
  startDate: string;
  endDate: string;
}

export interface ICompetingCampaignsResponse {
  campaigns: IGamificationCampaignDto[];
  truncated: boolean;
}

export interface IValidatePeriodParams {
  startDate: string;
  endDate: string;
  excludeId?: number;
  campaignType?: string;
  competes?: boolean;
  group?: string | null;
}

export interface ISlugAvailabilityParams {
  slug: string;
  excludeId?: number;
}

export interface ISlugAvailability {
  slug: string;
  available: boolean;
}

export const CampaignSaveStatus = {
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED',
} as const;

export type CampaignSaveStatus =
  (typeof CampaignSaveStatus)[keyof typeof CampaignSaveStatus];

export interface ICreateGamification {
  campaignName: string;
  slug?: string;
  startDate: string;
  endDate: string;
  stores: IStore[];
  mechanicConfig?: IMechanicConfig;
  faqItems?: IFaqItem[];
  status?: CampaignSaveStatus;

  segmentationFile?: File;
  regulations?: File;
  banner3x4?: File;
  banner16x9?: File;

  banner3x4SourceKey?: string;
  banner16x9SourceKey?: string;
  allStoresSelected?: boolean;
  campaignType?: CampaignType;
}

export interface IUpdateGamification {
  campaignName?: string;
  slug?: string;
  startDate?: string;
  endDate?: string;
  stores?: IStore[];
  mechanicConfig?: IMechanicConfig;
  faqItems?: IFaqItem[];
  status?: string;
  regulations?: File;
  banner3x4?: File;
  banner16x9?: File;

  banner3x4SourceKey?: string;
  banner16x9SourceKey?: string;
  allStoresSelected?: boolean;
  campaignType?: CampaignType;
}

export interface IGamificationFilters {
  search?: string;
  status?: string;
  period?: string;
  periodStart?: string;
  periodEnd?: string;
  campaignType?: CampaignType;
  sortBy?: GamificationSortBy;
  sortOrder?: 'ASC' | 'DESC';
  order?: {
    [key: string]: string;
  };
  meta?: {
    page?: number;
    perPage?: number;
  };
}

export interface ISegmentationInvalidRow {
  line: number;
  value: string;
  reason: string;
}

export interface ISegmentationResult {
  total: number;
  valid: number;
  invalid: number;
  duplicates: number;
  invalidRows: ISegmentationInvalidRow[];

  validCpfs: string[];
}

export const BannerGallerySource = {
  DEFAULT: 'DEFAULT',
  CAMPAIGN: 'CAMPAIGN',
} as const;

export type BannerGallerySource =
  (typeof BannerGallerySource)[keyof typeof BannerGallerySource];

export const BannerAspect = {
  WIDE: '16x9',
  PORTRAIT: '3x4',
} as const;

export type BannerAspect = (typeof BannerAspect)[keyof typeof BannerAspect];

export interface IBannerGalleryItem {
  key: string;
  source: BannerGallerySource;
  aspect: BannerAspect;
  url: string;
  campaignType: string | null;
  campaignId: number | null;
  campaignName: string | null;
}

export interface IBannerGalleryParams {
  campaignType?: CampaignType;
  search?: string;
}

export interface IBannerGalleryResponse {
  message: string;
  data: IBannerGalleryItem[];
}

export interface IConcurrencyGroupCampaign {
  id: number;
  idCapa: number | null;
  campaignName: string | null;
  customerFacingName: string | null;
  campaignType: Mechanic;
  status: string | null;
  startDate: string | null;
  endDate: string | null;
  priority: number;
}

export interface IConcurrencyGroup {
  name: string;
  campaignCount: number;
  activeCount: number;
  mechanics: Mechanic[];
  campaigns: IConcurrencyGroupCampaign[];
}

export interface IConcurrencyGroupsParams {
  search?: string;
  excludeCampaignId?: number;
}

export interface IConcurrencyGroupsResponse {
  message: string;
  data: IConcurrencyGroup[];
}

export const GamificationSettingKey = {
  CAMPAIGN_LOCATION_RADIUS_KM: 'CAMPAIGN_LOCATION_RADIUS_KM',
} as const;

export type GamificationSettingKey =
  (typeof GamificationSettingKey)[keyof typeof GamificationSettingKey];

export interface IGamificationSetting {
  key: GamificationSettingKey;
  value: string;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface IGamificationSettingsResponse {
  message: string;
  data: IGamificationSetting[];
}

export interface IGamificationSettingResponse {
  message: string;
  data: IGamificationSetting;
}

export interface IUpdateGamificationSettingParams {
  key: GamificationSettingKey;
  value: string;
}
